import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import { generatePresentationApi } from "../../services/presentation";
import { Toaster, toast } from "react-hot-toast";
import ButtonLoader from "../ButtonLoader";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";
import Estimation from "../Estimation";
import axios from "axios";
import TextArea from "../TextArea";
import { buildCustomerContactOptions } from "../PresentationProductsPopup/contactOptions";

import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Paper,
  Stack,
  Divider,
  Alert,
  Tooltip,
  Chip,
  Select,
  MenuItem,
  InputAdornment,
  FormControl,
  OutlinedInput,
} from "@mui/material";

import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import NotesIcon from "@mui/icons-material/Notes";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import FlagIcon from "@mui/icons-material/Flag";

const ymdFromAnything = (v) => {
  if (!v) return null;
  const s = String(v).trim();
  const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : null;
};
const coerceDayjs = (v) => {
  if (!v) return null;
  if (dayjs.isDayjs(v)) return v.isValid() ? v : null;
  if (v instanceof Date) {
    const d = dayjs(v);
    return d.isValid() ? d : null;
  }
  const ymd = ymdFromAnything(v);
  if (ymd) {
    const d = dayjs(ymd);
    return d.isValid() ? d : null;
  }
  const d = dayjs(String(v));
  return d.isValid() ? d : null;
};

const fieldInputSx = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    height: 40,
    borderRadius: "8px",
    backgroundColor: "background.paper",
    fontSize: "0.875rem",
    "& fieldset": { borderColor: "divider" },
    "&:hover fieldset": { borderColor: "text.disabled" },
    "&.Mui-focused fieldset": {
      borderColor: "primary.main",
      borderWidth: "1.5px",
    },
  },
  "& .MuiInputBase-input": {
    padding: "0 12px",
    height: 40,
    boxSizing: "border-box",
  },
};

const FieldLabel = ({ children, required }) => (
  <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.75 }}>
    <Typography
      variant="caption"
      sx={{
        color: "text.secondary",
        fontWeight: 600,
        fontSize: "0.75rem",
        lineHeight: 1,
      }}
    >
      {children}
    </Typography>
    {required && (
      <Chip
        label="Required"
        size="small"
        sx={{
          height: 16,
          fontSize: "0.6rem",
          fontWeight: 700,
          bgcolor: "rgba(199,9,9,0.08)",
          color: "primary.main",
          border: "1px solid rgba(199,9,9,0.15)",
          "& .MuiChip-label": { px: "5px" },
        }}
      />
    )}
  </Stack>
);

const currencyOptions = [{ name: "CAD", id: "CAD" }];

const PresentationScreen = ({
  order,
  activeTab,
  clientContacts,
  fetchOrder,
  conversionStatus = 1,
  handleShowProductsPopup,
  selectedProducts,
  createDocsLocked = false,
  createDocsLockTip = "",
  paidDepositApprovalAction = null,
}) => {
  const currentDate = dayjs();

  const [presentationInfo, setPresentationInfo] = useState({
    presentationDate: currentDate,
    inHandsDate: null,
    clientContact: "",
    currency: "CAD",
    hidePricing: false,
    hideProductTotals: true,
    showCatalogueDetails: true,
    introduction: "",
    sendEmail: false,
    products: [],
  });

  const { tabtype, orderId } = useParams();
  const [loading, setLoading] = useState(false);
  const [sanitizedContacts, setSanitizedContacts] = useState([]);
  const [addContactOpen, setAddContactOpen] = useState(false);
  const [savingContact, setSavingContact] = useState(false);
  const [newContact, setNewContact] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });

  const contactValue = (c) => c?.contactId || c?.id || "";
  const contactEmail = (c) => {
    const email = String(c?.email || "").trim();
    if (email) return email;
    const legacyId = String(c?.id || "").trim();
    return legacyId.includes("@") ? legacyId : "";
  };
  
  const handleSendEmailToggle = (event) => {
    setPresentationInfo((prev) => ({
      ...prev,
      sendEmail: event.target.checked,
    }));
  };

  const handleDateChange = (newDate, name) =>
    setPresentationInfo((prev) => ({ ...prev, [name]: newDate }));

  const handleSelect = (event, name) =>
    setPresentationInfo((prev) => ({ ...prev, [name]: event.target.value }));

  const handleInputChange = (event, name) =>
    setPresentationInfo((prev) => ({ ...prev, [name]: event.target.value }));

  const handleToggle = () =>
    setPresentationInfo((prev) => ({
      ...prev,
      hidePricing: !prev.hidePricing,
    }));

  const hasValidContacts =
    Array.isArray(clientContacts) &&
    clientContacts.length > 0 &&
    clientContacts[0].id !== "";

  const hasValidInHandsDate =
    presentationInfo?.inHandsDate &&
    dayjs(presentationInfo.inHandsDate).isValid();

  const primaryCustomerId = order?.attachedCustomers?.[0];

  const isGenerateDisabled =
    createDocsLocked || !hasValidContacts || !hasValidInHandsDate;

  const generateTooltip = createDocsLocked
    ? createDocsLockTip || "Creating documents is locked for this project."
    : !hasValidContacts
    ? "Please attach a client contact first."
    : !hasValidInHandsDate
    ? "Please fill in a valid In Hands Date."
    : "";

  const openCustomerPopup = () => {
    if (!primaryCustomerId) return;
    window.open(
      `/instance/elite-promo-marketing/sales/customers?openCustomer=${primaryCustomerId}`,
      "_blank",
    );
  };

  const handleCreateContact = async () => {
    if (!primaryCustomerId) return;
    if (!newContact.email?.trim()) {
      toast.error("Email is required for a new contact.");
      return;
    }
    setSavingContact(true);
    try {
      const headers = {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
      };
      await axios.post(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/customers/${primaryCustomerId}/client-contacts`,
        {
          firstName: newContact.firstName?.trim() || "",
          lastName: newContact.lastName?.trim() || "",
          email: newContact.email?.trim(),
          phone: newContact.phone?.trim() || "",
        },
        headers,
      );
      toast.success("Contact added successfully.");
      setAddContactOpen(false);
      setNewContact({ firstName: "", lastName: "", email: "", phone: "" });
      await fetchOrder?.();
    } catch (error) {
      console.error("Failed to create contact:", error);
      toast.error("Failed to add contact.");
    } finally {
      setSavingContact(false);
    }
  };

  const generatePresentaton = async () => {
    if (createDocsLocked) {
      toast.error(
        createDocsLockTip ||
          "Creating new presentation is locked for this project.",
      );
      return;
    }
    if (!hasValidContacts) {
      toast.error(
        "Please attach a customer contact before generating a presentation, estimate, or sales order.",
      );
      return;
    }
    if (!hasValidInHandsDate) {
      toast.error(
        "Please fill in a valid In Hands Date before generating a presentation.",
      );
      return;
    }
    try {
      setLoading(true);
      const products = order.Products.map((p) => ({
        id: p.id,
        asiId: p.ProductId,
      }));
      const presetationDetails = {
        presentationDate: presentationInfo?.presentationDate,
        inhandsDate: presentationInfo?.inHandsDate,
        currency: presentationInfo.currency,
        clientContactEmail:
          contactEmail(
            sanitizedContacts.find(
              (c) =>
                String(contactValue(c)) ===
                String(presentationInfo.clientContact),
            ),
          ) || presentationInfo.clientContact,
        productDetails: products,
        introduction: presentationInfo.introduction,
        orderId: order.OrderId,
        total: order?.Total?.amount,
        hidePrice: presentationInfo?.hidePricing ? 1 : 0,
        hideProductTotals: presentationInfo.hideProductTotals,
        showCatalogueDetails: presentationInfo.showCatalogueDetails,
        sendEmail: presentationInfo?.sendEmail === true,
        attachedCustomers: order.attachedCustomers ?? [],
      };
      const email = String(presetationDetails.clientContactEmail || "").trim();
      if (!/^[^\s@`]+@[^\s@`]+\.[^\s@`]+$/.test(email)) {
        setLoading(false);
        toast.error("Invalid client contact email. Please correct the contact email before generating a presentation.", { id: 7 });
        return;
      }
      presetationDetails.clientContactEmail = email;
      const res = await generatePresentationApi(presetationDetails);
      toast.success(res.data.message, { id: 5 });
      setLoading(false);
      window.location.reload();
    } catch (error) {
      setLoading(false);
      toast.error(
        error?.response?.data?.message || error?.message || "Unable to generate presentation.",
        { id: 7 },
      );
    }
  };

  useEffect(() => {
    const contacts = buildCustomerContactOptions(clientContacts);
    const normalizedInHands = dayjs(order?.inHandsDate);
    const defaultContact =
      contacts.find(
        (c) =>
          String(c.id) === String(order?.clientContactId || "") ||
          String(contactEmail(c)).toLowerCase() ===
            String(
              order?.presentation?.clientContactEmail ||
                order?.clientContactEmail ||
                "",
            ).toLowerCase(),
      ) || contacts[0];
    // The edit controls must reflect the newest saved presentation, not a hardcoded checked default.
    const presentations = Array.isArray(order?.presentation) ? order.presentation : [order?.presentation].filter(Boolean);
    const latest = [...presentations].sort((a, b) => new Date(b?.createdAt || 0) - new Date(a?.createdAt || 0))[0];
    const rawMeta = latest?.snapshotMeta;
    let meta = rawMeta && typeof rawMeta === "object" ? rawMeta : {};
    if (typeof rawMeta === "string") {
      try { meta = JSON.parse(rawMeta) || {}; } catch (_) { meta = {}; }
    }
    setPresentationInfo((prev) => ({
      ...prev,
      clientContact: defaultContact?.id || "",
      inHandsDate: normalizedInHands.isValid() ? normalizedInHands : null,
      ...(latest ? { showCatalogueDetails: meta.showCatalogueDetails !== false } : {}),
    }));
    setSanitizedContacts(contacts);
  }, [clientContacts, order?.inHandsDate, order?.presentation]);

  return (
    <>
      <Toaster position="top-right" />

      <div className="order-segment">
        {tabtype !== "presentation" ? (
          <Estimation
            presentationInfo={presentationInfo}
            handleDateChange={handleDateChange}
            handleSelect={handleSelect}
            clientContacts={clientContacts}
            currencyOptions={currencyOptions}
            handleInputChange={handleInputChange}
            generatePresentaton={generatePresentaton}
            orderId={orderId}
            loading={loading}
            order={order}
            fetchOrder={fetchOrder}
            conversionStatus={conversionStatus}
            paidDepositApprovalAction={paidDepositApprovalAction}
          />
        ) : (
          <form onSubmit={(e) => e.preventDefault()}>
            <Paper
              elevation={0}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderTop: "3px solid",
                borderTopColor: "primary.main",
                borderRadius: "10px",
                overflow: "hidden",
                boxShadow:
                  "0 1px 3px 0 rgba(0,0,0,0.06), 0 1px 2px -1px rgba(0,0,0,0.06)",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 2.5,
                  py: 1.5,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  bgcolor: "background.paper",
                  flexWrap: "wrap",
                  gap: 1,
                }}
              >
                <Box>
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    sx={{
                      lineHeight: 1.2,
                      color: "text.primary",
                      fontSize: "1rem",
                    }}
                  >
                    Presentation Details
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary" }}
                  >
                    Provide key information to tailor your presentation for the
                    client.
                  </Typography>
                </Box>

                <Tooltip
                  title={generateTooltip}
                  arrow
                  disableHoverListener={!isGenerateDisabled}
                >
                  <span>
                    <Button
                      variant="contained"
                      onClick={generatePresentaton}
                      disabled={isGenerateDisabled}
                      startIcon={
                        loading ? null : (
                          <AutoAwesomeIcon sx={{ fontSize: 15 }} />
                        )
                      }
                      sx={{
                        textTransform: "none",
                        borderRadius: "8px",
                        fontWeight: 600,
                        fontSize: "0.875rem",
                        height: 36,
                        boxShadow: "none",
                        whiteSpace: "nowrap",
                        minWidth: 185,
                        "&.Mui-disabled": { opacity: 0.5 },
                      }}
                    >
                      {loading ? <ButtonLoader /> : "Generate Presentation"}
                    </Button>
                  </span>
                </Tooltip>
              </Box>

              {!hasValidContacts && (
                <Box sx={{ px: 2.5, pt: 2, pb: 0 }}>
                  <Alert
                    severity="warning"
                    icon={<WarningAmberIcon fontSize="small" />}
                    sx={{
                      borderRadius: "8px",
                      fontSize: "0.8125rem",
                      "& .MuiAlert-message": {
                        display: "flex",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 0.5,
                      },
                    }}
                  >
                    This customer has no contacts attached.
                    <Button
                      size="small"
                      startIcon={<PersonAddAltIcon sx={{ fontSize: 13 }} />}
                      onClick={() => setAddContactOpen(true)}
                      sx={{
                        textTransform: "none",
                        fontWeight: 700,
                        fontSize: "0.78rem",
                        p: "2px 8px",
                        borderRadius: "6px",
                        ml: 0.5,
                      }}
                    >
                      Add Contact
                    </Button>
                    {primaryCustomerId && (
                      <Button
                        size="small"
                        startIcon={<OpenInNewIcon sx={{ fontSize: 13 }} />}
                        onClick={openCustomerPopup}
                        sx={{
                          textTransform: "none",
                          fontWeight: 700,
                          fontSize: "0.78rem",
                          p: "2px 8px",
                          borderRadius: "6px",
                        }}
                      >
                        Open Customer
                      </Button>
                    )}
                  </Alert>
                </Box>
              )}

              <Box
                sx={{
                  p: 2.5,
                  display: "flex",
                  flexDirection: "column",
                  gap: 2.5,
                }}
              >
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "1fr 1fr",
                      lg: "1fr 1fr 1fr 1fr",
                    },
                    gap: 2,
                    alignItems: "end",
                  }}
                >
                  <Box>
                    <FieldLabel>
                      <CalendarTodayIcon
                        sx={{ fontSize: 11, mr: 0.4, verticalAlign: "middle" }}
                      />
                      Presentation Date
                    </FieldLabel>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        value={coerceDayjs(presentationInfo.presentationDate)}
                        onChange={(newVal) =>
                          handleDateChange(
                            coerceDayjs(newVal),
                            "presentationDate",
                          )
                        }
                        slots={{ openPickerIcon: CalendarTodayIcon }}
                        slotProps={{
                          textField: {
                            size: "small",
                            fullWidth: true,
                            sx: fieldInputSx,
                          },
                          openPickerIcon: {
                            sx: { fontSize: 16, color: "text.secondary" },
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </Box>

                  <Box>
                    <FieldLabel required>
                      <FlagIcon
                        sx={{ fontSize: 11, mr: 0.4, verticalAlign: "middle" }}
                      />
                      Requested In Hands Date
                    </FieldLabel>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        value={coerceDayjs(presentationInfo.inHandsDate)}
                        minDate={dayjs()}
                        onChange={(newVal) =>
                          handleDateChange(coerceDayjs(newVal), "inHandsDate")
                        }
                        slots={{ openPickerIcon: CalendarTodayIcon }}
                        slotProps={{
                          textField: {
                            size: "small",
                            fullWidth: true,
                            error: !hasValidInHandsDate,
                            sx: {
                              ...fieldInputSx,
                              ...(!hasValidInHandsDate && {
                                "& .MuiOutlinedInput-root": {
                                  ...fieldInputSx["& .MuiOutlinedInput-root"],
                                  "& fieldset": { borderColor: "error.light" },
                                },
                              }),
                            },
                          },
                          openPickerIcon: {
                            sx: { fontSize: 16, color: "text.secondary" },
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </Box>

                  <Box>
                    <FieldLabel required>
                      <PersonOutlineIcon
                        sx={{ fontSize: 11, mr: 0.4, verticalAlign: "middle" }}
                      />
                      Client Contact
                    </FieldLabel>
                    {hasValidContacts ? (
                      <Select
                        fullWidth
                        size="small"
                        value={presentationInfo.clientContact}
                        onChange={(e) => handleSelect(e, "clientContact")}
                        displayEmpty
                        IconComponent={KeyboardArrowDownIcon}
                        renderValue={(value) => {
                          const found = sanitizedContacts.find(
                            (o) => o.id === value,
                          );
                          return found ? (
                            <Typography
                              sx={{
                                fontSize: "0.875rem",
                                color: "text.primary",
                                lineHeight: "40px",
                              }}
                            >
                              {found.name}
                            </Typography>
                          ) : (
                            <Typography
                              sx={{
                                fontSize: "0.875rem",
                                color: "text.disabled",
                              }}
                            >
                              Select contact
                            </Typography>
                          );
                        }}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              borderRadius: "8px",
                              boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
                              mt: 0.5,
                              "& .MuiMenuItem-root": {
                                fontSize: "0.875rem",
                                py: 1,
                                "&:hover": { bgcolor: "action.hover" },
                                "&.Mui-selected": {
                                  bgcolor: "rgba(199,9,9,0.06)",
                                  fontWeight: 600,
                                  "&:hover": { bgcolor: "rgba(199,9,9,0.1)" },
                                },
                              },
                            },
                          },
                          anchorOrigin: {
                            vertical: "bottom",
                            horizontal: "left",
                          },
                          transformOrigin: {
                            vertical: "top",
                            horizontal: "left",
                          },
                        }}
                        sx={{
                          height: 40,
                          borderRadius: "8px",
                          fontSize: "0.875rem",
                          bgcolor: "background.paper",
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "divider",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "text.disabled",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "primary.main",
                            borderWidth: "1.5px",
                          },
                          "& .MuiSelect-icon": {
                            fontSize: 18,
                            color: "text.secondary",
                          },
                        }}
                      >
                        <MenuItem disabled value="">
                          <Typography
                            sx={{
                              fontSize: "0.875rem",
                              color: "text.disabled",
                            }}
                          >
                            Select contact
                          </Typography>
                        </MenuItem>
                        {sanitizedContacts.map((option, index) => (
                          <MenuItem key={index} value={option.id}>
                            {option.name}
                          </MenuItem>
                        ))}
                      </Select>
                    ) : (
                      <Box
                        sx={{
                          height: 40,
                          border: "1px dashed",
                          borderColor: "warning.main",
                          borderRadius: "8px",
                          display: "flex",
                          alignItems: "center",
                          px: 1.5,
                          bgcolor: "rgba(255,152,0,0.03)",
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ color: "warning.dark" }}
                        >
                          No contacts — add one above
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  <Box>
                    <FieldLabel>
                      <AttachMoneyIcon
                        sx={{ fontSize: 11, mr: 0.4, verticalAlign: "middle" }}
                      />
                      Currency
                    </FieldLabel>
                    <Select
                      fullWidth
                      size="small"
                      value={presentationInfo.currency}
                      onChange={(e) => handleSelect(e, "currency")}
                      IconComponent={KeyboardArrowDownIcon}
                      renderValue={(value) => {
                        const found = currencyOptions.find(
                          (o) => o.id === value,
                        );
                        return (
                          <Typography
                            sx={{ fontSize: "0.875rem", color: "text.primary" }}
                          >
                            {found ? found.name : value}
                          </Typography>
                        );
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            borderRadius: "8px",
                            boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
                            mt: 0.5,
                            "& .MuiMenuItem-root": {
                              fontSize: "0.875rem",
                              py: 1,
                              "&.Mui-selected": {
                                bgcolor: "rgba(199,9,9,0.06)",
                                fontWeight: 600,
                              },
                            },
                          },
                        },
                        anchorOrigin: {
                          vertical: "bottom",
                          horizontal: "left",
                        },
                        transformOrigin: {
                          vertical: "top",
                          horizontal: "left",
                        },
                      }}
                      sx={{
                        height: 40,
                        borderRadius: "8px",
                        fontSize: "0.875rem",
                        bgcolor: "background.paper",
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "divider",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "text.disabled",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "primary.main",
                          borderWidth: "1.5px",
                        },
                        "& .MuiSelect-icon": {
                          fontSize: 18,
                          color: "text.secondary",
                        },
                      }}
                    >
                      {currencyOptions.map((option) => (
                        <MenuItem key={option.id} value={option.id}>
                          {option.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>
                </Box>

                <Divider>
                  <Chip
                    label="Content & Options"
                    size="small"
                    icon={<NotesIcon sx={{ fontSize: "12px !important" }} />}
                    sx={{
                      fontSize: "0.7rem",
                      fontWeight: 600,
                      color: "text.secondary",
                      bgcolor: "background.paper",
                      border: "1px solid",
                      borderColor: "divider",
                      "& .MuiChip-label": { px: "8px" },
                    }}
                  />
                </Divider>

    <Box onClick={() => setPresentationInfo((prev) => ({ ...prev, showCatalogueDetails: !prev.showCatalogueDetails }))}
      sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, py: 1.25,
        border: "1px solid", borderColor: "divider", borderRadius: "8px", bgcolor: "action.hover",
        cursor: "pointer", userSelect: "none" }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Tooltip title={presentationInfo.showCatalogueDetails ? "Size / colour source: Supplier catalogue" : "Size / colour source: Order Cart"}>
          <PaletteOutlinedIcon sx={{ fontSize: 17, color: "text.secondary" }} />
        </Tooltip>
        <Typography variant="body2" fontWeight={600}>Show Supplier Catalogue Details</Typography>
        <Tooltip title="Show available supplier sizes and colours instead of the selected Order Cart variations. This choice is saved with this revision and does not affect other revisions.">
          <InfoOutlinedIcon onClick={(e) => e.stopPropagation()} sx={{ fontSize: 17, color: "text.secondary" }} />
        </Tooltip>
      </Stack>
      <Checkbox size="small" sx={{ p: 0 }} checked={presentationInfo.showCatalogueDetails}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => setPresentationInfo((prev) => ({ ...prev, showCatalogueDetails: e.target.checked }))} />
    </Box>

    <Box onClick={() => setPresentationInfo(p => ({ ...p, hideProductTotals: !p.hideProductTotals }))} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1.25, border: '1px solid', borderColor: 'divider', borderRadius: '8px', bgcolor: 'action.hover', cursor: 'pointer' }}>
      <Box><Typography variant="body2" fontWeight={600}>Hide Product Totals</Typography><Typography variant="caption" color="text.secondary">Hide each product subtotal, tax and total; keep unit prices and presentation total.</Typography></Box>
      <Checkbox checked={presentationInfo.hideProductTotals} onClick={e => e.stopPropagation()} onChange={e => setPresentationInfo(p => ({ ...p, hideProductTotals: e.target.checked }))} size="small" />
    </Box>
    <Box
      onClick={handleToggle}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: 2,
        py: 1.25,
        border: "1px solid",
        borderColor: presentationInfo.hidePricing
          ? "rgba(199,9,9,0.3)"
          : "divider",
        borderRadius: "8px",
        bgcolor: presentationInfo.hidePricing
          ? "rgba(199,9,9,0.03)"
          : "action.hover",
        cursor: "pointer",
        transition: "border-color 0.15s, background-color 0.15s",
        userSelect: "none",
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.25}>
        <VisibilityOffIcon
          sx={{
            fontSize: 18,
            color: presentationInfo.hidePricing
              ? "primary.main"
              : "text.disabled",
            transition: "color 0.15s",
          }}
        />
        <Box>
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{ lineHeight: 1.2, color: "text.primary" }}
          >
            Hide Pricing
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            Hide pricing from the customer-facing presentation. Changes apply to all versions of this presentation, including previous revisions. Regeneration is not required.
          </Typography>
        </Box>
      </Stack>

      <Checkbox
        checked={presentationInfo.hidePricing}
        onChange={handleToggle}
        onClick={(e) => e.stopPropagation()}
        color="primary"
        size="small"
        sx={{ p: 0 }}
      />
    </Box>

    <Box
      onClick={() =>
        handleSendEmailToggle({
          target: { checked: !presentationInfo.sendEmail },
        })
      }
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: 2,
        py: 1.25,
        border: "1px solid",
        borderColor: presentationInfo.sendEmail
          ? "rgba(199,9,9,0.3)"
          : "divider",
        borderRadius: "8px",
        bgcolor: presentationInfo.sendEmail
          ? "rgba(199,9,9,0.03)"
          : "action.hover",
        cursor: "pointer",
        transition: "border-color 0.15s, background-color 0.15s",
        userSelect: "none",
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.25}>
        <AutoAwesomeIcon
          sx={{
            fontSize: 18,
            color: presentationInfo.sendEmail
              ? "primary.main"
              : "text.disabled",
            transition: "color 0.15s",
          }}
        />
        <Box>
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{ lineHeight: 1.2, color: "text.primary" }}
          >
            Send Presentation Email
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            Email the generated presentation to the selected client contact
          </Typography>
        </Box>
      </Stack>

      <Checkbox
        checked={presentationInfo.sendEmail}
        onChange={handleSendEmailToggle}
        onClick={(e) => e.stopPropagation()}
        color="primary"
        size="small"
        sx={{ p: 0 }}
      />
    </Box>



    <Box>
      <Typography
        variant="caption"
        sx={{
          color: "text.secondary",
          fontWeight: 600,
          mb: 0.75,
          display: "block",
          fontSize: "0.75rem",
        }}
      >
        <NotesIcon sx={{ fontSize: 11, mr: 0.4, verticalAlign: "middle" }} />
        Presentation Title / Introduction
      </Typography>

      <TextArea
        label="Presentation Title"
        value={presentationInfo.introduction}
        name="introduction"
        handleInputChange={handleInputChange}
        inputPadding="12px"
      />
    </Box>



              </Box>
            </Paper>
          </form>
        )}
      </div>

      <Dialog
        open={addContactOpen}
        onClose={() => setAddContactOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
            border: "1px solid",
            borderColor: "divider",
          },
        }}
      >
        <DialogTitle
          sx={{
            pt: 2.5,
            pb: 1.5,
            px: 3,
            display: "flex",
            alignItems: "flex-start",
            gap: 1.25,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "8px",
              bgcolor: "rgba(199,9,9,0.08)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <PersonAddAltIcon sx={{ fontSize: 18, color: "primary.main" }} />
          </Box>
          <Box>
            <Typography
              variant="h6"
              fontWeight={700}
              sx={{ lineHeight: 1.2, fontSize: "1rem" }}
            >
              Add Contact
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              This contact will be attached to the current customer.
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ px: 3, pt: "20px !important", pb: 1 }}>
          <Stack spacing={2}>
            <Box
              sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}
            >
              <TextField
                label="First Name"
                size="small"
                value={newContact.firstName}
                onChange={(e) =>
                  setNewContact((prev) => ({
                    ...prev,
                    firstName: e.target.value,
                  }))
                }
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
              />
              <TextField
                label="Last Name"
                size="small"
                value={newContact.lastName}
                onChange={(e) =>
                  setNewContact((prev) => ({
                    ...prev,
                    lastName: e.target.value,
                  }))
                }
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
              />
            </Box>
            <TextField
              label="Email"
              type="email"
              required
              size="small"
              value={newContact.email}
              onChange={(e) =>
                setNewContact((prev) => ({ ...prev, email: e.target.value }))
              }
              helperText="Required — used to identify this contact"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
            />
            <TextField
              label="Phone"
              size="small"
              value={newContact.phone}
              onChange={(e) =>
                setNewContact((prev) => ({ ...prev, phone: e.target.value }))
              }
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
            />
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: "1px solid",
            borderColor: "divider",
            gap: 1,
          }}
        >
          <Button
            onClick={() => setAddContactOpen(false)}
            variant="outlined"
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              fontWeight: 600,
              minWidth: 88,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateContact}
            variant="contained"
            disabled={savingContact}
            startIcon={
              savingContact ? null : <PersonAddAltIcon sx={{ fontSize: 15 }} />
            }
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              fontWeight: 600,
              boxShadow: "none",
              minWidth: 130,
            }}
          >
            {savingContact ? <ButtonLoader /> : "Add Contact"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PresentationScreen;
